import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors, isXS, isHovered, compact) => StyleSheet.create({
  container: {
    width: compact ? '100%' : isXS ? 140 : 160,
    marginRight: compact ? 0 : 12,
    marginBottom: compact ? 0 : undefined,
  },
  box: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: compact ? BORDER_RADIUS.small : BORDER_RADIUS.medium,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: isHovered ? colors.accent_color_primary : colors.border_color_secondary,
    backgroundColor: isHovered ? colors.accent_color_primary + '08' : colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { transitionDuration: '150ms' }),
  },
  label: {
    marginTop: compact ? 4 : 8,
    textAlign: 'center',
  },
});

/**
 * A style-sized card with a plus icon and "Create your own" label.
 * Use at the end of Image/Music/Video style carousels for free-form creation.
 */
const CreateYourOwnStyleCard = ({ onPress, label = 'Create your own', compact = false }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [isHovered, setIsHovered] = useState(false);

  const styles = useMemo(() => createStyles(colors, isXS, isHovered, compact), [colors, isXS, isHovered, compact]);

  const handlePress = useCallback(() => {
    onPress?.();
  }, [onPress]);

  return (
    <Pressable
      style={styles.container}
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={styles.box}>
        <Icon
          name="plus"
          size={compact ? 22 : 28}
          color={isHovered ? colors.accent_color_primary : colors.text_color_secondary}
        />
      </View>
      <Text
        size={compact ? 'xs' : 's'}
        weight="medium"
        color="secondary"
        style={styles.label}
        numberOfLines={compact ? 1 : 2}
      >
        {label}
      </Text>
    </Pressable>
  );
};

export default React.memo(CreateYourOwnStyleCard);
